import { createBrowserRouter, redirect, useRouteError } from 'react-router-dom';
import { lazy, Suspense, useState } from 'react';
import NotFound from './pages/NotFound';
import {
    AdditionalPayResultType,
    createAccountByToken,
    CreateOrderSheetReturnType,
    ExtraChagreType,
    getMallUserIdByToken,
    GroupPurchaseOrderType,
    Inquiries,
    MallBusinessInfoType,
    MallInfo,
    MallNotification,
    MallUserInfoType,
    Mileage,
    OnlineCouponListType,
    OrderDetail,
    OrderDetailV2,
    ProductDetailsType,
    ShippingAddressBookType,
    UserCouponListType,
    UserOrder,
    UserOrderV2,
} from './services/mallUser.service';

import { axiosApi } from './helpers/api.helper';
import {
    checkIsMallUser,
    checkRefreshToken,
    checkRefreshTokenBool,
    getTokenByAnonymous,
    isLogined,
    logOut,
    setReferrerRefreshToken,
    setReferrerToken,
} from './services/auth.service';
import { getProductDetail } from './services/matching.service';
import { queryClient } from './lib/queryClient';
import { vendorQueryOptions } from './hooks/useVendorInfo';
import { productListQueryOptions } from './hooks/useProductList';
import { PurchaseDrawerStoreState } from './store/purchaseDrawerStore';
import { useSourcingReferrerStore } from './store/sourcingReferrerStore';
import isMobile from 'is-mobile';
import { useAnonymousUserStore, useUserStore } from './store/userStore';
import { useVendorAuthStore } from './store/vendorAuthStore';

// Lazy load pages (로딩 시간 최적화)
const DDMHome = lazy(() => import('./pages/DDM/Home/DDMHome'));
const Main2 = lazy(() => import('./pages/DDM/Home/MainPage2'));
const MainPageMobile = lazy(() => import('./pages/DDM/Home/MainPageMobile'));
const ProductDetailPage = lazy(() => import('./pages/DDM/Product/ProductDetailPage'));
const CheckoutPage = lazy(() => import('./pages/DDM/Checkout/CheckoutPage'));
const OrderComplete = lazy(() => import('./pages/DDM/Checkout/OrderComplete'));
const OrdersPage = lazy(() => import('./pages/DDM/Orders/OrdersPage'));
const ResellerOrderDetail = lazy(() => import('./pages/DDM/Orders/ResellerOrderDetail'));
const ReturnsPage = lazy(() => import('./pages/DDM/Orders/ReturnsPage'));
const BackorderPage = lazy(() => import('./pages/DDM/Orders/BackorderPage'));
const SamplePage = lazy(() => import('./pages/DDM/Orders/SamplePage'));
const CartPage = lazy(() => import('./pages/DDM/Cart/CartPage'));
const MypagePage = lazy(() => import('./pages/DDM/Mypage/MypagePage'));
const VendorRegister = lazy(() => import('./pages/DDM/Auth/VendorRegister'));
const AccountSettingsPage = lazy(() => import('./pages/DDM/Account/AccountSettingsPage'));
const VendorSettingsPage = lazy(() => import('./pages/DDM/Settings/VendorSettingsPage'));
const SubscriptionSettingsPage = lazy(() => import('./pages/DDM/Settings/SubscriptionSettingsPage'));
const SocialLoginComponent = lazy(() => import('./pages/DDM/Auth/SocialLogin'));
const BillingReturnHandler = lazy(() =>
    import('./pages/DDM/Billing/BillingReturnHandler').then((m) => ({ default: m.BillingReturnHandler })),
);
const SubscriptionPage = lazy(() => import('./pages/DDM/Subscription/SubscriptionPage'));
const SubscriptionCheckoutPage = lazy(() => import('./pages/DDM/Subscription/SubscriptionCheckoutPage'));
const SubscriptionSuccessPage = lazy(() => import('./pages/DDM/Subscription/SubscriptionSuccessPage'));
const CashChargePage = lazy(() => import('./pages/DDM/Cash/CashChargePage'));
const CashHistoryPage = lazy(() => import('./pages/DDM/Cash/CashHistoryPage'));
const DirectOrderPage = lazy(() => import('./pages/DDM/DirectOrder/DirectOrderPage'));
const DepositHistoryPage = lazy(() => import('./pages/DDM/Deposit/DepositHistoryPage'));
const LikeListPage = lazy(() => import('./pages/DDM/Like/LikeListPage'));
const Shop3PlansPage = lazy(() => import('./pages/DDM/Subscription/Shop3PlansPage'));
const Shop3SubscriptionManagePage = lazy(() => import('./pages/DDM/Subscription/Shop3SubscriptionPage'));

const env = import.meta.env.VITE_SET_ENV || 'DEV';

async function ensureVendorData(vendorPath: string) {
    if (!vendorPath) return null;
    return queryClient.ensureQueryData(vendorQueryOptions(vendorPath));
}

async function ensureProductListData(params: {
    vendorId: string;
    tab?: string;
}) {
    if (!params.vendorId) return null;
    return queryClient.ensureQueryData(productListQueryOptions(params));
}

const router = createBrowserRouter([
    {
        path: '/',
        element: isMobile() ? <MainPageMobile /> : <Main2 />,
        // element: <MainPageMobile />,
        // element: <Main2 />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/mobile',
        element: <MainPageMobile />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/:vendorPath',
        element: <DDMHome />,
        id: 'vendorRoute',
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const loaderStart = performance.now();

            await sessionCheckFunc().then((r) => r);
            console.log(`[DDMHome loader] sessionCheckFunc: ${(performance.now() - loaderStart).toFixed(0)}ms`);

            const { vendorPath } = params;
            const vendorStart = performance.now();
            const vendor = await ensureVendorData(vendorPath || '');
            console.log(`[DDMHome loader] ensureVendorData: ${(performance.now() - vendorStart).toFixed(0)}ms`);
            if (!vendor) {
                return redirect('/');
            }
            const productStart = performance.now();
            const productList = await ensureProductListData({ vendorId: vendor.id });
            console.log(`[DDMHome loader] productLoader: ${(performance.now() - productStart).toFixed(0)}ms`);

            console.log(`[DDMHome loader] TOTAL: ${(performance.now() - loaderStart).toFixed(0)}ms`);
            return {
                vendor,
                productList,
            };
        },
    },
    {
        path: '/:vendorPath/products/:productGroupId',
        element: <ProductDetailPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);

            const { vendorPath, productGroupId } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }

            try {
                const product = await getProductDetail({ productGroupId: productGroupId || '' });
                if (!product) {
                    return redirect(`/${vendorPath}`);
                }
                return product;
            } catch (error) {
                console.error('Product fetch error:', error);
                return redirect(`/${vendorPath}`);
            }
        },
    },
    {
        path: '/:vendorPath/all',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            const productList = await ensureProductListData({ vendorId: vendor.id });
            return {
                vendor,
                productList,
            };
        },
    },
    {
        path: '/:vendorPath/new',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            const productList = await ensureProductListData({ vendorId: vendor.id, tab: 'new' });
            return {
                vendor,
                productList,
            };
        },
    },
    {
        path: '/:vendorPath/best-new',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            const productList = await ensureProductListData({ vendorId: vendor.id, tab: 'bestNew' });
            return {
                vendor,
                productList,
            };
        },
    },
    {
        path: '/:vendorPath/today-new',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            const productList = await ensureProductListData({ vendorId: vendor.id, tab: 'todayNew' });
            return {
                vendor,
                productList,
            };
        },
    },
    {
        path: '/:vendorPath/discount',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            const productList = await ensureProductListData({ vendorId: vendor.id, tab: 'discount' });
            return {
                vendor,
                productList,
            };
        },
    },
    {
        path: '/:vendorPath/sold-out',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            const productList = await ensureProductListData({ vendorId: vendor.id, tab: 'soldOut' });
            return {
                vendor,
                productList,
            };
        },
    },
    {
        path: '/:vendorPath/cart',
        element: <CartPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/vendor-register',
        element: <VendorRegister />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/checkout',
        element: <CheckoutPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/order-complete/:orderId',
        element: <OrderComplete />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/orders',
        element: <OrdersPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/orders/:orderId',
        element: <ResellerOrderDetail />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/returns',
        element: <ReturnsPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/backorder',
        element: <BackorderPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/sample',
        element: <SamplePage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/mypage',
        element: <MypagePage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/settings',
        element: <VendorSettingsPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/settings/subscription',
        element: <SubscriptionSettingsPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/cash/charge',
        element: <CashChargePage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/cash/history',
        element: <CashHistoryPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/deposit/history',
        element: <DepositHistoryPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/direct-order',
        element: <DirectOrderPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/likes',
        element: <LikeListPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/subscription/plans',
        element: <Shop3PlansPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/:vendorPath/subscription/manage',
        element: <Shop3SubscriptionManagePage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);
            const { vendorPath } = params;
            const vendor = await ensureVendorData(vendorPath || '');
            if (!vendor) {
                return redirect('/');
            }
            return { vendor };
        },
    },
    {
        path: '/subscription',
        element: <SubscriptionPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/promo',
        element: <SubscriptionPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/subscription/checkout',
        element: <SubscriptionCheckoutPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/subscription/success',
        element: <SubscriptionSuccessPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/billing/return',
        element: <BillingReturnHandler />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/account/settings',
        element: <AccountSettingsPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: 'social/:socialName',
        element: <SocialLoginComponent />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { socialName: social } = params;
            const queryString = new URLSearchParams(window.location.search);
            const path = String(queryString.get('state')).split('/')[2];
            try {
                if (isLogined()) {
                    return redirect(!!path ? `/mall/${path}` : `/mall`);
                }
                return { social };
            } catch (error) {
                throw error;
            }
        },
    },
    {
        path: '/app-ads.txt',
        element: <p>google.com, pub-2774361938949385, DIRECT, f08c47fec0942fa0</p>,
    },
]);

export interface PersonalnfoLoaderReturnType {
    userInfo: MallUserInfoType;
    shippingList: ShippingAddressBookType[];
}

export interface MallMainLoader {
    mallInfo: MallInfo['mallInfo'];
    tagNames: string[];
    notificationList: MallNotification['notificationList'];
    mallBusinessInfo: MallBusinessInfoType['mallBusinessInfo'];
    shopName: string;
}
export interface ProductDetailsLoader {
    productDetails: ProductDetailsType;
    drawerProductList: PurchaseDrawerStoreState['drawerProductList'];
    mallInfo: MallInfo['mallInfo'];
    mallBusinessInfo: MallBusinessInfoType['mallBusinessInfo'];
    shopName: string;
}
export interface OrderNPayLoaderReturnType {
    shippingAddressBookList: ShippingAddressBookType[];
    mileageList: Mileage['mileageList'];
    userInfo: MallUserInfoType;
    mallId: string;
    orderInfo: CreateOrderSheetReturnType['orderInfo'];
    couponList: UserCouponListType['items'];
    // salesPolicyInfo: SalesPolicyType,
    state: 'success' | 'fail';
    pgType: string;
    pgBusinessType: string;
    shoppingPoint: number;
    userPoint: number;
    shoppingPointPolicy: {
        id: string;
        policyName: string;
        targetUserType: string;
        pointConversionRate: number;
        maxUsageRate: number;
        minConversionAmount: number;
        minUsageAmount: number;
        isActive: boolean;
        priority: number;
        startDateTime: string;
        endDateTime: string;
    };
}
export interface FooterInfoDataType {
    mallBusinessInfo: MallBusinessInfoType['mallBusinessInfo'];
    shopName: string;
    mallId: string;
    mallInfo: MallInfo['mallInfo'];
}
export interface DestinationListLoaderReturnType {
    shippingAddressBookList: ShippingAddressBookType[];
}
export interface DestinationAdditionLoaderReturnType {
    userInfo: MallUserInfoType;
}
export interface MypageLoaderReturnType {
    userInfo: MallUserInfoType;
    mileage: number;
    mallId: string;
    state: 'success' | 'fail';
}
export interface PersonalnfoLoaderReturnType {
    userInfo: MallUserInfoType;
    shippingList: ShippingAddressBookType[];
}
export interface OrderNShippingInfoReturnType {}
export interface AdditionalPayReturnType {
    extraChargeInfo: ExtraChagreType['extraChargeInfo'];
}
export interface AdditionalPayResultReturnType {
    payResult: AdditionalPayResultType['extraChargeInfo'];
    paramData: {
        accountNumber: string;
        bank: string;
        customerName: string;
        dueDate: string;
        failMessage: string;
    };
}
export interface PayResultReturnType {
    orderDetailInfo: OrderDetailV2['item'];
    paramData: {
        accountNumber: string;
        bank: string;
        customerName: string;
        dueDate: string;
        failMessage: string;
        depositor: string;
    };
    couponList: OnlineCouponListType[];
    pgType: 'TOSS' | 'NICE';
}

/**
 * @description
 * `비회원일 경우 api_key를 이용해 토큰과 ID를 useAnonymousUserStore에 저장합니다.`
 */
export async function fetchNStoreAnonymousTokenNId() {
    const { reset: userReset } = useUserStore.getState();
    /* 비회원일 경우 토큰 발급 */
    const isAnonymousToken = useAnonymousUserStore.getState().accessToken ? true : false;
    // if (!isLogined() && !isAnonymousToken) {
    userReset();
    const { access_token } = await getTokenByAnonymous();
    axiosApi.defaults.headers.common.Authorization = `Bearer ${access_token}`;
    const { mallUserId } = await createAccountByToken(access_token);
    useAnonymousUserStore.setState({
        mallUserId,
        accessToken: access_token,
    });
}

async function sessionCheckFunc(notRefer = true) {
    // 이미 로그인된 경우 세션 체크 스킵
    if (isLogined()) {
        // 도매 로그인 시 Authorization 헤더 복원 (새로고침 후 토큰 유실 방지)
        const { accessToken: vendorAccessToken } = useVendorAuthStore.getState();
        if (vendorAccessToken) {
            axiosApi.defaults.headers.common.Authorization = `Bearer ${vendorAccessToken}`;
        }
        return;
    }

    // URL에 토큰 파라미터가 있는 경우 토큰 로그인 진행 중이므로 비회원 토큰 생성 스킵
    const urlParams = new URLSearchParams(window.location.search);
    const hasTokenParam = urlParams.get('token');
    if (hasTokenParam) {
        return;
    }

    try {
        const isToken = await checkRefreshTokenBool().then((r) => r.data);
        if (isToken) {
            const { reset: anonymousUserReset } = useAnonymousUserStore.getState();
            anonymousUserReset();
            try {
                const { access_token, expires_in } = await checkRefreshToken()
                    .then((r) => r)
                    .catch((err) => {
                        throw err;
                    });
                useUserStore.setState({
                    accessToken: access_token,
                    expiresIn: expires_in,
                });
                axiosApi.defaults.headers.common.Authorization = `Bearer ${access_token}`;
                const isMallUser = await checkIsMallUser(access_token).then((r) => r.data);
                if (isMallUser) {
                    const { mallUserId } = await getMallUserIdByToken(access_token).then((r) => r);
                    useUserStore.setState({ mallUserId });
                } else {
                    const { mallUserId } = await createAccountByToken(access_token).then((r) => r);
                    useUserStore.setState({ mallUserId });
                }
            } catch (err) {
                await logOut();
                window.location.reload();
                throw err;
            }
        } else {
            await fetchNStoreAnonymousTokenNId();
        }
        if (!notRefer) return;
        const queryString = new URLSearchParams(window.location.search);

        // 출처 - 후킹스프 기여도
        const utm_source = queryString.get('utm_source') || '';
        // 매체 - 쇼츠 피드를 보고 넘어옴
        const utm_medium = queryString.get('utm_medium') || '';
        // 인스타셀러 아이디 or 플로잉 닉네임 - 누가 등록한 영상을 보고 넘어왔는가
        // (크롤링 쇼츠의 경우 sns 아이디, 직접 등록한 경우 플로잉 닉네임)
        const utm_campaign = queryString.get('utm_campaign') || '';
        // 상품 아이디 - 해당 상품 구매에만 referrerToken 사용 가능
        const utm_campaign_id = queryString.get('utm_campaign_id') || '';
        const utm_referrer = queryString.get('utm_referrer') || '';
        const utm_content = queryString.get('utm_content') || '';
        const utm_term = queryString.get('utm_term') || '';

        const referer = queryString.get('referer') || '';
        const user_id = queryString.get('user_id') || '';

        const isValidQuery =
            utm_source ||
            utm_medium ||
            utm_campaign ||
            utm_referrer ||
            referer ||
            utm_campaign_id ||
            user_id ||
            utm_content ||
            utm_term;

        const queryPayload = {
            utm_source,
            utm_medium,
            utm_campaign,
            utm_referrer,
            utm_campaign_id,
            user_id,
            referer,
            utm_content,
            utm_term,
        };

        if (isValidQuery) {
            const token: string =
                (await setReferrerToken({ ...queryPayload })
                    .then((r) => r)
                    .catch(() => '')) || '';
            useSourcingReferrerStore.setState({
                referrer_token: token,
            });
        } else {
            const token: string =
                (await setReferrerRefreshToken({ ...queryPayload })
                    .then((r) => r)
                    .catch(() => '')) || '';
            useSourcingReferrerStore.setState({
                referrer_token: token,
            });
        }
    } catch (error) {
        const err: any = error;
        if (!err.status || !err.statusCode) {
            throw { status: -1 };
        }
        try {
            await logOut();
            window.location.reload();
        } finally {
        }
    }
}
export interface OrderListLoaderType {
    orderList: UserOrder[];
    totalCount: number | null;
    isNextLists: boolean;
    isIssue: 'yes' | '';
}
export interface OrderListLoaderV2Type {
    items: UserOrderV2[];
    totalCount: number | null;
    isNextLists: boolean;
    isIssue: boolean;
}

export interface MyInquiryListLoaderType {
    lists: Inquiries['inquiryList'];
    groupPurchaseList: GroupPurchaseOrderType[];
    totalCount: number;
}

export interface NoticePageLoaderType {
    lists: MallNotification['notificationList'];
}

export interface OrderDetailInfoLoaderType {
    orderDetail: OrderDetail['orderDetailInfo'] | 'null';
    isIsland: boolean;
    isJeju: boolean;
    noData?: boolean;
    couponList: OnlineCouponListType[];
}

export interface OrderDetailInfoLoaderTypeV2 {
    orderDetail: OrderDetailV2['item'] | 'null';
    isIsland: boolean;
    isJeju: boolean;
    noData?: boolean;
    couponList: OnlineCouponListType[];
}

interface Designdata {
    spacehome_create_step: number;
    design_type: string;
    bg_filter_type: string;
    bg_type: string;
    description: string;
    sub_description: null;
    bg_color: string;
    text_color: string;
    button_color: string;
    profile_img_url: string;
    banner_img_url: string;
    bg_img_url: string;
    is_button_animated: boolean;
    is_theme: boolean;
    path: string;
}
export interface SpaceHomeV2LoaderReturnType {
    design_data: Designdata;
    item_list: any;
    user_id: string;
    is_mall_opened: boolean;
    nickname: string;
}

function ErrorComponent() {
    const error: any = useRouteError();
    console.log('error::::', error);
    return <NotFound error={error} />;
}

export default router;
